import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiClient } from '@app/lib/api/api-client';
import {
  APPROVAL_SOCIAL_PERMISSION_PRIORITY,
  MODERATE_MODULE,
  PRIORITY_CONTEXT,
  TAG_CONTEXTS
} from '@app/shared/const';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { OBJECT_TYPE_REPORT } from '../enums/approval-requests.enum';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';

export interface TagContext {
  value: string;
  nextValue?: string;
  label: string;
  shortenLabel?: string;
  styleClass: string;
}

export interface PriorityContext {
  label: string;
  color: string;
  code: string;
}

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private api: ApiClient;

  constructor(
    private http: HttpClient,
    private translateService: TranslationService,
    private toastService: ToastService
  ) {
    this.api = new ApiClient(this.http, { responseTimeout: environment.API_TIMEOUT });
  }

  private isOpenSubject = new BehaviorSubject<boolean>(false);
  private apiUrl = environment.API_URL;
  isOpen$ = this.isOpenSubject.asObservable();

  toggle() {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  getImageUrl(id: string | undefined): string {
    if (!id) {
      return '';
    }
    const isPath = id.startsWith('/');
    const webPath = isPath ? '' : '/storage/files/web/';
    const addWebp = isPath ? '' : '.webp';

    return `${this.apiUrl}${webPath}${id}${addWebp}`;
  }

  getEntityAvatar(entityData: any, objectType: string): string {
    let avatar = '';

    switch (objectType) {
      case OBJECT_TYPE_REPORT.FANPAGE:
        avatar = entityData?.object_data?.avatar;
        break;

      case OBJECT_TYPE_REPORT.GROUP:
        avatar = entityData?.object_data?.icon_image_uri;
        break;

      case OBJECT_TYPE_REPORT.ADVERTISEMENT:
        avatar = entityData?.object_data?.page_info?.avatar;
        break;

      case MODERATE_MODULE.ota:
      case OBJECT_TYPE_REPORT.SHOPPING:
        avatar = entityData?.child_data?.avatar_property_name;
        break;

      default:
        avatar =
          entityData?.owner_object?.avatar ||
          entityData?.object_data?.user_object?.avatar_thumbnail_url ||
          entityData?.object_data?.created_by?.avatar_thumbnail_url ||
          entityData?.user_object?.avatar_thumbnail_url ||
          entityData?.object_data?.avatar ||
          entityData?.object_data?.icon_image_uri;
        break;
    }

    return this.getImageUrl(avatar);
  }

  ensureURLProtocol(url: string): string {
    const protocolRegex = /^(?:(?:https?|ftp):\/\/)/i;
    if (!protocolRegex.test(url)) {
      url = 'https://' + url;
    }
    return url;
  }

  extractDomain(url: string): string {
    let domain = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i);
    if (domain && domain.length > 0) {
      return domain[2];
    }
    return '';
  }

  checkHlsVideo(mediaId: string): Observable<any> {
    return this.api.storage.checkHlsVideo(mediaId);
  }

  getPenaltyPointStyleClass(point?: number): string {
    if (!point || !Number.isFinite(point)) return '';

    return point <= 7
      ? 'text-palette-emerald-600 border-palette-emerald-500 bg-palette-emerald-50'
      : point <= 12
        ? 'text-palette-yellow-600 border-palette-yellow-500 bg-palette-yellow-50'
        : point <= 15
          ? 'text-palette-red-600 border-palette-red-500 bg-palette-red-50'
          : '';
  }

  getTagContext(value: string): TagContext {
    if (!value) return TAG_CONTEXTS.none;

    const findTagContext = Object.values(TAG_CONTEXTS).find(
      (item: TagContext) => item.value === value.toUpperCase()
    );

    return findTagContext ?? { ...TAG_CONTEXTS.default, label: value };
  }

  getPriorityStyleClass(priority: string): PriorityContext | undefined {
    return Object.values(PRIORITY_CONTEXT).find(item => item.code === priority);
  }

  showModerationToastMessage(action: string, objectType: string, isSuccess: boolean) {
    const actionTranslateKey = action.toUpperCase();

    const translatedModuleName = this.translateService
      .getTranslation(`APPROVAL_REQUESTS.MODULE_LIST.SOCIAL_LIST.${objectType}`)
      .toLocaleLowerCase();

    this.toastService.apiResponse(
      isSuccess,
      `APPROVAL_REQUESTS.${actionTranslateKey}_SUCCESS`,
      `APPROVAL_REQUESTS.${actionTranslateKey}_FAILURE`,
      {
        params: { module: translatedModuleName },
        defaultErrorMessage:
          objectType === OBJECT_TYPE_REPORT.PRODUCT && !isSuccess
            ? 'The product is deleted'
            : undefined
        //TODO: This defaultErrorMessage cloned from approval-requests-report.component.ts. Should translate this message
      }
    );
  }

  hasAnyPermission(permissionList: string[], allowedRoles: string[]): boolean {
    return allowedRoles.some(role => permissionList?.includes(role));
  }

  requiredPermission(permissionList: string[], requiredRoles: string[]): boolean {
    return requiredRoles.every(role => permissionList?.includes(role));
  }

  getPriorityByPermission(permissionList: string[]): string[] {
    return permissionList
      .map((permission: string) => APPROVAL_SOCIAL_PERMISSION_PRIORITY[permission])
      .filter(Boolean);
  }
}
